import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum, curSym, fmtDate, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { exportXlsx } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Checkbox } from "@finance/components/ui/checkbox";
import { DownloadSimple, FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";
import { DueChip } from "@finance/components/DueChip";
import { Pending1470 } from "@finance/components/Pending1470";

function BalanceCell({ value, normal }) {
  // signed: positive = debit balance. Show Dr/Cr suffix for GL; for supplier positive = we owe.
  const abs = Math.abs(value);
  const side = normal === "supplier" ? (value >= 0 ? "Cr" : "Dr") : value >= 0 ? "Dr" : "Cr";
  return <span className="lx-num">{fmtNum(abs, "TZS")} <span className="text-[10px] text-gray-400">{side}</span></span>;
}

function StatementTable({ data, mode, pdfPath, onOpenRow }) {
  if (!data) return <div className="py-12 text-center text-sm text-gray-400">Select an account to view its statement.</div>;
  const showPo = mode !== "gl";
  const poLabel = mode === "client" ? "Client PO" : "Somcore PO";
  const descKey = mode === "supplier" ? "description" : "memo";
  const cols = [
    { key: "date", label: "Date", format: (v) => fmtDate(v) },
    { key: "type", label: "Type" },
    { key: "reference", label: "Reference" },
    ...(showPo ? [{ key: "po_no", label: poLabel }] : []),
    { key: descKey, label: "Description" },
    { key: "debit", label: "Debit", format: fmtNum },
    { key: "credit", label: "Credit", format: fmtNum },
    { key: "balance", label: "Balance", format: fmtNum },
  ];
  const openingSpan = showPo ? 7 : 6;
  const emptySpan = showPo ? 8 : 7;
  const totalSpan = showPo ? 5 : 4;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{mode === "gl" ? `${data.code} · ${data.name}` : (data.supplier || data.client)}</span>
          <span className="ml-3">Opening: <span className="lx-num font-medium">{fmtNum(data.opening, "TZS")}</span></span>
          <span className="ml-3">Closing: <span className="lx-num font-semibold text-[#1B4383]">{fmtNum(data.closing, "TZS")}</span></span>
        </div>
        <div className="flex gap-2">
          {pdfPath && (
            <Button variant="outline" data-testid="statement-pdf" onClick={() => openPdf(pdfPath)}>
              <FilePdf size={16} className="mr-1" />PDF
            </Button>
          )}
          <Button variant="outline" data-testid="statement-export"
            onClick={() => exportXlsx(`statement-${mode}`, data.rows, cols)} disabled={!data.rows.length}>
            <DownloadSimple size={16} className="mr-1" />Excel
          </Button>
        </div>
      </div>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Reference</th>
              {showPo && <th className="px-4 py-2 text-left">{poLabel}</th>}
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">Debit</th>
              <th className="px-4 py-2 text-right">Credit</th>
              <th className="px-4 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <td className="px-4 py-2 text-gray-500" colSpan={openingSpan}>Opening balance</td>
              <td className="px-4 py-2 text-right lx-num font-medium">{fmtNum(data.opening, "TZS")}</td>
            </tr>
            {data.rows.length === 0 && (
              <TableEmpty colSpan={emptySpan} title="No entries in this period" message="Adjust the period to see statement activity." />
            )}
            {data.rows.map((r, i) => (
              <tr key={i} data-testid={`statement-row-${i}`}
                onClick={() => r.source_type && onOpenRow && onOpenRow(r)}
                className={`border-b border-gray-100 hover:bg-gray-50 ${r.source_type ? "cursor-pointer" : ""}`}
                title={r.source_type ? "Click to open & amend the source" : ""}>
                <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-4 py-2">
                  <span className={`rounded-sm px-1.5 py-0.5 text-[11px] ${r.type === "Payment" ? "bg-emerald-50 text-emerald-700" : r.type === "Bill" ? "bg-orange-50 text-orange-700" : "bg-gray-100 text-gray-600"}`}>
                    {r.type || r.source_type || "—"}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-600">{r.reference || "—"}</td>
                {showPo && <td className="px-4 py-2 lx-num text-gray-600" data-testid={`statement-po-${i}`}>{r.po_no || "—"}</td>}
                <td className="px-4 py-2 text-gray-700">{r[descKey] || "—"}</td>
                <td className="px-4 py-2 text-right lx-num">{r.debit ? fmtNum(r.debit, "TZS") : "—"}</td>
                <td className="px-4 py-2 text-right lx-num">{r.credit ? fmtNum(r.credit, "TZS") : "—"}</td>
                <td className="px-4 py-2 text-right lx-num font-medium">{fmtNum(r.balance, "TZS")}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-gray-300 font-bold">
              <td className="px-4 py-2" colSpan={totalSpan}>TOTAL</td>
              <td className="px-4 py-2 text-right lx-num">{fmtNum(data.total_debit, "TZS")}</td>
              <td className="px-4 py-2 text-right lx-num">{fmtNum(data.total_credit, "TZS")}</td>
              <td className="px-4 py-2 text-right lx-num text-[#1B4383]">{fmtNum(data.closing, "TZS")}</td>
            </tr>
          </tbody>
        </table>
        {data.memos && data.memos.length > 0 && (
          <table className="w-full text-sm" data-testid="statement-memos">
            <tbody>
              <tr><td colSpan={7} className="px-4 pt-4 pb-1 lx-label text-gray-400">Memo — advances, prepayments and amounts paid before EFD (1470) — not included in the balance above</td></tr>
              {data.memos.map((m, i) => (
                <tr key={`m${i}`} className="border-b border-dashed border-gray-100 text-gray-500" data-testid={`statement-memo-${i}`}>
                  <td className="px-4 py-1.5 whitespace-nowrap">{fmtDate(m.date)}</td>
                  <td className="px-4 py-1.5"><span className="rounded-sm bg-purple-50 px-1.5 py-0.5 text-[11px] text-purple-700">{m.type}</span></td>
                  <td className="px-4 py-1.5">{m.reference || "—"}</td>
                  <td className="px-4 py-1.5">{m.description}</td>
                  <td className="px-4 py-1.5 text-right lx-num" colSpan={2}>{curSym(m.currency)} {fmtNum(m.amount)}</td>
                  <td className="px-4 py-1.5 text-right text-[11px] text-gray-400">memo</td>
                </tr>
              ))}
              <tr className="font-semibold text-purple-700"><td colSpan={4} className="px-4 py-1.5">Total memo (advances / prepayments / pending EFD)</td><td colSpan={3} className="px-4 py-1.5 text-right lx-num" data-testid="statement-memo-total">{fmtNum(data.memo_total, "TZS")}</td></tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DateFilters({ start, end, setStart, setEnd, onApply }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <Label className="lx-label">From</Label>
        <DateField value={start} data-testid="statement-start" onChange={(e) => setStart(e.target.value)} className="mt-1 w-40" />
      </div>
      <div>
        <Label className="lx-label">To</Label>
        <DateField value={end} data-testid="statement-end" onChange={(e) => setEnd(e.target.value)} className="mt-1 w-40" />
      </div>
      <Button variant="secondary" data-testid="statement-apply" onClick={onApply}>Apply</Button>
    </div>
  );
}

function ViewToggle({ view, setView, onRunning, onBillwise, testid }) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 p-0.5" data-testid={testid}>
      <button data-testid={`${testid}-running`} className={`rounded px-3 py-1 text-sm font-medium transition-colors ${view === "running" ? "bg-[#1B4383] text-white" : "text-gray-600 hover:bg-gray-100"}`} onClick={() => { setView("running"); onRunning && onRunning(); }}>Running balance</button>
      <button data-testid={`${testid}-billwise`} className={`rounded px-3 py-1 text-sm font-medium transition-colors ${view === "billwise" ? "bg-[#1B4383] text-white" : "text-gray-600 hover:bg-gray-100"}`} onClick={() => { setView("billwise"); onBillwise && onBillwise(); }}>Bill-wise</button>
    </div>
  );
}

function BillwiseTable({ data, mode, onOpenRow, pdfPath, onEmail }) {
  if (!data) return <div className="py-12 text-center text-sm text-gray-400">Select an account to view its outstanding bills.</div>;
  const label = mode === "client" ? "Invoice" : "Bill";
  const cols = [
    { key: "reference", label: `${label} No` },
    { key: "date", label: "Date", format: (v) => fmtDate(v) },
    { key: "due_date", label: "Due", format: (v) => (v ? fmtDate(v) : "—") },
    { key: "type", label: "Type" },
    { key: "po_no", label: mode === "client" ? "Client PO" : "Somcore PO" },
    { key: "original", label: "Original", format: fmtNum },
    { key: "settled", label: "Settled", format: fmtNum },
    { key: "outstanding", label: "Outstanding", format: fmtNum },
  ];
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{data.party}</span>
          <span className="ml-3">Outstanding: <span className="lx-num font-semibold text-[#1B4383]" data-testid="billwise-total">{fmtNum(data.total_outstanding, "TZS")}</span></span>
          <span className="ml-3 text-gray-500">{data.bills.length} open {label.toLowerCase()}{data.bills.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex gap-2">
          {onEmail && (
            <Button variant="outline" data-testid="billwise-email" onClick={onEmail} disabled={!data.bills.length}>
              <PaperPlaneTilt size={16} className="mr-1" />Email
            </Button>
          )}
          {pdfPath && (
            <Button variant="outline" data-testid="billwise-pdf" onClick={() => openPdf(pdfPath)} disabled={!data.bills.length}>
              <FilePdf size={16} className="mr-1" />PDF
            </Button>
          )}
          <Button variant="outline" data-testid="billwise-export" onClick={() => exportXlsx(`billwise-${mode}`, data.bills, cols)} disabled={!data.bills.length}>
            <DownloadSimple size={16} className="mr-1" />Excel
          </Button>
        </div>
      </div>
      {data.aging && data.total_outstanding > 0 && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="billwise-aging">
          {[["0-30", "0–30 days", "bg-emerald-50 text-emerald-700"], ["31-60", "31–60 days", "bg-amber-50 text-amber-700"], ["61-90", "61–90 days", "bg-orange-50 text-orange-700"], ["90+", "90+ days", "bg-red-50 text-red-700"]].map(([k, lbl, cls]) => (
            <div key={k} className={`rounded-md px-3 py-2 ${cls}`} data-testid={`billwise-bucket-${k}`}>
              <div className="text-[11px] font-medium uppercase tracking-wide opacity-80">{lbl}</div>
              <div className="lx-num text-sm font-semibold">{fmtNum(data.aging[k] || 0, "TZS")}</div>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">{label} No</th>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Due</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">{mode === "client" ? "Client PO" : "Somcore PO"}</th>
              <th className="px-4 py-2 text-right">Original</th>
              <th className="px-4 py-2 text-right">Settled</th>
              <th className="px-4 py-2 text-right">Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {data.bills.length === 0 && (
              <TableEmpty colSpan={8} title="All settled" message={`No outstanding ${label.toLowerCase()}s — every bill for this account is fully paid.`} testid="billwise-empty" />
            )}
            {data.bills.map((b, i) => (
              <tr key={i} data-testid={`billwise-row-${i}`}
                onClick={() => b.source_type && onOpenRow && onOpenRow(b)}
                className={`border-b border-gray-100 hover:bg-gray-50 ${b.source_type ? "cursor-pointer" : ""}`}
                title={b.source_type ? "Click to open the source document" : ""}>
                <td className="px-4 py-2 font-mono-num font-medium">{b.reference}</td>
                <td className="px-4 py-2 whitespace-nowrap">{fmtDate(b.date)}<span className="ml-1 text-[11px] text-gray-400">· {b.age_days}d</span></td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-600" data-testid={`billwise-due-${i}`}>{b.due_date ? fmtDate(b.due_date) : "—"}{b.outstanding > 0 && b.due_date && <DueChip date={b.due_date} testid={`billwise-due-chip-${i}`} />}</td>
                <td className="px-4 py-2 text-gray-600">{b.type}</td>
                <td className="px-4 py-2 lx-num text-gray-600">{b.po_no || "—"}</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(b.original, "TZS")}</td>
                <td className="px-4 py-2 text-right lx-num text-emerald-700">{b.settled ? fmtNum(b.settled, "TZS") : "—"}</td>
                <td className="px-4 py-2 text-right lx-num font-semibold text-[#1B4383]">{fmtNum(b.outstanding, "TZS")}</td>
              </tr>
            ))}
            {data.bills.length > 0 && (
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="px-4 py-2" colSpan={5}>TOTAL</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(data.total_original, "TZS")}</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(data.total_settled, "TZS")}</td>
                <td className="px-4 py-2 text-right lx-num text-[#1B4383]">{fmtNum(data.total_outstanding, "TZS")}</td>
              </tr>
            )}
          </tbody>
        </table>
        {data.on_account > 0.01 && (
          <div className="border-t border-dashed border-gray-200 px-4 py-2 text-xs text-gray-500" data-testid="billwise-onaccount">
            On-account (unallocated {mode === "client" ? "receipts" : "payments"}): <span className="lx-num font-semibold text-purple-700">{fmtNum(data.on_account, "TZS")}</span> — allocate these against bills to clear them.
          </div>
        )}
      </div>
    </div>
  );
}

export default function Statements() {
  const navigate = useNavigate();
  const openRow = async (r) => {
    if (!r.source_type || !r.source_id) return;
    try {
      const { data } = await api.get("/gl/source-link", { params: { source_type: r.source_type, source_id: r.source_id } });
      if (data.route) navigate(data.route);
      else toast.info("This entry has no editable source.");
    } catch { toast.error("Could not open the source record."); }
  };
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [account, setAccount] = useState("");
  const [supplier, setSupplier] = useState("");
  const [client, setClient] = useState("");
  const [glData, setGlData] = useState(null);
  const [supData, setSupData] = useState(null);
  const [cliData, setCliData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [supView, setSupView] = useState("running");
  const [cliView, setCliView] = useState("running");
  const [supBill, setSupBill] = useState(null);
  const [cliBill, setCliBill] = useState(null);

  const loadSupBill = useCallback((sid) => {
    if (!sid) return;
    const params = {};
    if (end) params.end = end;
    api.get(`/suppliers/${sid}/billwise`, { params }).then((r) => setSupBill(r.data));
  }, [end]);

  const loadCliBill = useCallback((cid) => {
    if (!cid) return;
    const params = {};
    if (end) params.end = end;
    api.get(`/clients/${cid}/billwise`, { params }).then((r) => setCliBill(r.data));
  }, [end]);

  const emailSupBill = (sid) => openEmailDlg("supplier", sid);
  const emailCliBill = (cid) => openEmailDlg("client", cid);

  const [emailDlg, setEmailDlg] = useState({ open: false, kind: "", id: "", party: "", to: "", cc: true });
  const [sendingEmail, setSendingEmail] = useState(false);
  const openEmailDlg = (kind, id) => {
    if (!id) return;
    const arr = kind === "client" ? clients : suppliers;
    const rec = arr.find((x) => x.id === id) || {};
    setEmailDlg({ open: true, kind, id, party: rec.name || "", to: rec.email || "", cc: true });
  };
  const sendBillEmail = async () => {
    const { kind, id, to, cc } = emailDlg;
    if (!to.trim()) { toast.error("Enter at least one email address."); return; }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/${kind}s/${id}/billwise/email`, { email: to, cc_accounts: cc, end: end || undefined });
      const ccNote = (data.cc || []).length ? ` (cc ${data.cc.join(", ")})` : "";
      toast.success(`Statement emailed to ${(data.sent_to || []).join(", ")}${ccNote}`);
      setEmailDlg({ open: false, kind: "", id: "", party: "", to: "", cc: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the statement email.");
    } finally { setSendingEmail(false); }
  };

  useEffect(() => {
    api.get("/gl/accounts").then((r) => setAccounts(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const loadGl = useCallback((code) => {
    if (!code) return;
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get(`/gl/statement?account=${code}`, { params }).then((r) => setGlData(r.data));
  }, [start, end]);

  const loadSup = useCallback((sid) => {
    if (!sid) return;
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get(`/suppliers/${sid}/statement`, { params }).then((r) => setSupData(r.data));
  }, [start, end]);

  const loadCli = useCallback((cid) => {
    if (!cid) return;
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get(`/clients/${cid}/statement`, { params }).then((r) => setCliData(r.data));
  }, [start, end]);

  const dateQs = (sep) => {
    const p = [];
    if (start) p.push(`start=${start}`);
    if (end) p.push(`end=${end}`);
    return p.length ? `${sep}${p.join("&")}` : "";
  };

  return (
    <Layout>
      <PageHeader title="Statements" subtitle="Pull a running-balance statement for any GL account or supplier" />
      <div className="p-8">
        <Tabs defaultValue="gl">
          <TabsList data-testid="statements-tabs">
            <TabsTrigger value="gl" data-testid="tab-gl-statement">GL Account</TabsTrigger>
            <TabsTrigger value="supplier" data-testid="tab-supplier-statement">Supplier / Creditor</TabsTrigger>
            <TabsTrigger value="client" data-testid="tab-client-statement">Client / Debtor</TabsTrigger>
            <TabsTrigger value="p1470" data-testid="tab-1470-statement">1470 Pending EFD</TabsTrigger>
          </TabsList>

          <TabsContent value="p1470" className="mt-6"><Pending1470 /></TabsContent>

          <TabsContent value="gl" className="mt-6 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[280px]">
                <Label className="lx-label">GL Account</Label>
                <Select value={account} onValueChange={(v) => { setAccount(v); loadGl(v); }}>
                  <SelectTrigger className="mt-1" data-testid="gl-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DateFilters start={start} end={end} setStart={setStart} setEnd={setEnd} onApply={() => loadGl(account)} />
            </div>
            <StatementTable data={glData} mode="gl" onOpenRow={openRow} pdfPath={account ? `pdf/gl-statement?account=${account}${dateQs("&")}` : null} />
          </TabsContent>

          <TabsContent value="supplier" className="mt-6 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[280px]">
                <Label className="lx-label">Supplier</Label>
                <div className="mt-1 flex items-center gap-1">
                  <SearchableSelect className="flex-1" testid="supplier-select" placeholder="Select supplier"
                    value={supplier} onValueChange={(v) => { setSupplier(v); loadSup(v); loadSupBill(v); }}
                    options={suppliers.map((s) => ({ value: s.id, label: s.name }))} />
                  <QuickAddButton type="supplier" onCreated={(s) => { setSuppliers((prev) => [...prev, s]); setSupplier(s.id); loadSup(s.id); loadSupBill(s.id); }} />
                </div>
              </div>
              <DateFilters start={start} end={end} setStart={setStart} setEnd={setEnd} onApply={() => { loadSup(supplier); loadSupBill(supplier); }} />
            </div>
            <ViewToggle view={supView} setView={setSupView} testid="supplier-view-toggle"
              onRunning={() => loadSup(supplier)} onBillwise={() => loadSupBill(supplier)} />
            {supView === "running"
              ? <StatementTable data={supData} mode="supplier" onOpenRow={openRow} pdfPath={supplier ? `pdf/supplier-statement/${supplier}${dateQs("?")}` : null} />
              : <BillwiseTable data={supBill} mode="supplier" onOpenRow={openRow}
                  pdfPath={supplier ? `pdf/supplier-billwise/${supplier}${end ? `?end=${end}` : ""}` : null}
                  onEmail={() => emailSupBill(supplier)} />}
          </TabsContent>

          <TabsContent value="client" className="mt-6 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[280px]">
                <Label className="lx-label">Client</Label>
                <div className="mt-1 flex items-center gap-1">
                  <SearchableSelect className="flex-1" testid="client-select" placeholder="Select client"
                    value={client} onValueChange={(v) => { setClient(v); loadCli(v); loadCliBill(v); }}
                    options={clients.map((c) => ({ value: c.id, label: c.name }))} />
                  <QuickAddButton type="client" onCreated={(c) => { setClients((prev) => [...prev, c]); setClient(c.id); loadCli(c.id); loadCliBill(c.id); }} />
                </div>
              </div>
              <DateFilters start={start} end={end} setStart={setStart} setEnd={setEnd} onApply={() => { loadCli(client); loadCliBill(client); }} />
            </div>
            <ViewToggle view={cliView} setView={setCliView} testid="client-view-toggle"
              onRunning={() => loadCli(client)} onBillwise={() => loadCliBill(client)} />
            {cliView === "running"
              ? <StatementTable data={cliData} mode="client" onOpenRow={openRow} pdfPath={client ? `pdf/client-statement/${client}${dateQs("?")}` : null} />
              : <BillwiseTable data={cliBill} mode="client" onOpenRow={openRow}
                  pdfPath={client ? `pdf/client-billwise/${client}${end ? `?end=${end}` : ""}` : null}
                  onEmail={() => emailCliBill(client)} />}
          </TabsContent>
        </Tabs>

        <Dialog open={emailDlg.open} onOpenChange={(o) => setEmailDlg((d) => ({ ...d, open: o }))}>
          <DialogContent data-testid="billwise-email-dialog">
            <DialogHeader>
              <DialogTitle>Email statement — {emailDlg.party}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="billwise-email-to">Recipient(s)</Label>
              <Input id="billwise-email-to" data-testid="billwise-email-input"
                value={emailDlg.to}
                onChange={(e) => setEmailDlg((d) => ({ ...d, to: e.target.value }))}
                placeholder="name@company.com, finance@company.com" />
              <p className="text-xs text-gray-500">Separate multiple addresses with commas. Prefilled with the account's email on file.</p>
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <Checkbox checked={emailDlg.cc} onCheckedChange={(v) => setEmailDlg((d) => ({ ...d, cc: !!v }))} data-testid="billwise-email-cc" />
                CC accounts team (keep a copy on file)
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDlg((d) => ({ ...d, open: false }))} data-testid="billwise-email-cancel">Cancel</Button>
              <Button onClick={sendBillEmail} disabled={sendingEmail} data-testid="billwise-email-send">
                <PaperPlaneTilt size={16} className="mr-1" />{sendingEmail ? "Sending…" : "Send"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
